/**
 * Emissions Activity Form Page
 *
 * Thin page wrapper around `ActivityFormFlow`. Owns the Dashboard chrome, the
 * page header, and the "where to go after exit" decision (navigate to the
 * activities list).
 */

import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { generateUrl } from '@routes/util';
import { emissionsTrackerRoutes } from '../routes';
import { ActivityFormFlow } from '../components/form/ActivityFormFlow';
import { DataMethod } from '../components/form/types';
import './EmissionsActivityFormPage.scss';

const VALID_METHODS: DataMethod[] = Object.values(DataMethod);

export const EmissionsActivityFormPage = () => {
  const { initiativeId = '', activityId } = useParams<{
    initiativeId: string;
    activityId?: string;
  }>();
  const history = useHistory();
  const location = useLocation();
  const queryMethodRaw = new URLSearchParams(location.search).get('method');
  const queryMethod = VALID_METHODS.includes(queryMethodRaw as DataMethod)
    ? (queryMethodRaw as DataMethod)
    : undefined;

  const handleExit = () => {
    history.push(generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITIES, { initiativeId }));
  };

  const isEditing = Boolean(activityId);

  return (
    <Dashboard>
      <div className='tw:flex tw:items-center tw:gap-3 tw:mb-6'>
        <div>
          <h4 className='tw:text-xl tw:font-semibold tw:mb-1'>
            {isEditing ? 'Edit Activity' : 'Add Activities'}
          </h4>
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-0'>
            {isEditing
              ? 'Update the emission activity record'
              : 'Add emissions data to your inventory'}
          </p>
        </div>
      </div>
      <ActivityFormFlow
        initiativeId={initiativeId}
        activityId={activityId}
        initialMethod={queryMethod}
        onExit={handleExit}
      />
    </Dashboard>
  );
};
